<template>
  <div>
    <v-row density="compact">
      <v-col>
        <core-filters-sidebar
          ref="filtersBar"
          v-model:params-object="filtersParamsObject"
          :filters="filters"
          :report_id="6"
          :filters-counts="filtersCounts"
        />
      </v-col>
    </v-row>

    <helpers-section-header>
      <v-row density="compact">
        <v-col>
          Results
          <span v-if="loading" class="text-grey">
            (<v-progress-circular indeterminate color="grey" size="16" width="1" class="mx-1" />)
          </span>
          <span v-else class="text-grey">({{ totalItems }})</span>
        </v-col>
        <v-spacer />
        <v-btn text color="primary" class="ma-0 px-2" @click="customizeColumnsDialog = true">
          Customize columns
        </v-btn>
      </v-row>
    </helpers-section-header>

    <v-card>
      <v-data-table-server
        v-model:options="pagination"
        v-model:sort-by="pagination.sortBy"
        :headers="visibleTableHeaders"
        :items="tableItems"
        :items-length="totalItems"
        :items-per-page-options="[10, 25, 50, 100]"
        :loading="loading"
        :no-data-text="loading ? 'Loading...' : 'No results found.'"
        must-sort
      >
        <template #item.shares_held="{ item }">
          <span v-if="item.shares_held">{{ whole_number(item.shares_held) }}</span>
        </template>
        <template #item.change_shares="{ item }">
          <span v-if="item.change_shares">{{ whole_number(item.change_shares) }}</span>
        </template>
        <template #item.change_value="{ item }">
          <span v-if="item.change_value">{{ whole_number(item.change_value) }}</span>
        </template>
        <template #item.market_value="{ item }">
          <span v-if="item.market_value">{{ whole_number(item.market_value) }}</span>
        </template>
        <template #item.pct_portfolio="{ item }">
          <span v-if="item.pct_portfolio">{{ percent(item.pct_portfolio) }}</span>
        </template>
        <template #item.pct_shares_outstanding="{ item }">
          <span v-if="item.pct_shares_outstanding">{{ percent(item.pct_shares_outstanding) }}</span>
        </template>
        <template #item.organization_name="{ item }">
          <span v-if="item.perm_id_organization_id">
            <router-link :to="'/global/organization/' + item.perm_id_organization_id">
              {{ item.organization_name[0] }}
            </router-link>
          </span>
          <span v-else>{{ item.organization_name[0] }}</span>
        </template>
        <template #item.name="{ item }">
          <span v-if="item.perm_id_instrument_id">
            <router-link :to="'/global/instrument/' + item.perm_id_instrument_id">
              {{ item.name[0] }}
            </router-link>
          </span>
          <span v-else>{{ item.name }}</span>
        </template>
      </v-data-table-server>
      <small>Source: Refinitiv</small>
    </v-card>

    <v-dialog v-model="customizeColumnsDialog" width="400">
      <v-card>
        <v-card-text>
          <v-checkbox
            v-for="header in tableHeaders"
            :key="header.key"
            v-model="header.visible"
            :label="header.title"
            hide-details
            class="mt-0"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useAppStore, useWwStore } from '@/store'
import api from '@/store/api'
import FormattersMixin from '@/mixins/formatters'
import cloneDeep from 'lodash/cloneDeep'

export default {
  mixins: [FormattersMixin],

  data() {
    return {
      customizeColumnsDialog: false,
      loading: false,
      pagination: {
        sortBy: [{ key: 'holdings_date', order: 'desc' }],
        page: 1,
        itemsPerPage: 25
      },
      totalItems: 0,
      tableHeaders: [
        { title: 'Stock Name', key: 'name', visible: true },
        { title: 'Ticker', key: 'ticker', visible: true },
        { title: 'Owner', key: 'organization_name', visible: true },
        { title: 'Type of Investor', key: 'investor_type', visible: true },
        { title: 'Rank', key: 'rank', visible: true },
        { title: 'Shares Held', key: 'shares_held', visible: true },
        { title: '% Portfolio', key: 'pct_portfolio', visible: true },
        { title: '% Shares Outstanding', key: 'pct_shares_outstanding', visible: true },
        { title: 'Market Value', key: 'market_value', visible: true },
        { title: 'Date', key: 'holdings_date', visible: true },
        { title: 'Source', key: 'filing_type', visible: true },
        { title: 'Change in Shares', key: 'change_shares', visible: true },
        { title: 'Change in Value', key: 'change_value', visible: true },
        { title: 'Change in % Portfolio', key: 'change_pct', visible: true },
        { title: 'Issuer Country', key: 'issuer_country', visible: true },
        { title: 'Owner Country', key: 'owner_country', visible: true },
        { title: 'Owner City', key: 'owner_city', visible: true },
        { title: 'Exchange', key: 'issuer_exchange', visible: false },
        { title: 'Parent Investor Type', key: 'investor_parent_type', visible: false },
        { title: 'Investment Style', key: 'investment_style', visible: false },
        { title: 'Orientation', key: 'orientation', visible: false },
        { title: 'Turnover Rating', key: 'turnover_rate', visible: false }
      ],
      tableItems: [],
      filters: [
        { identifier: 'stock_ticker', title: 'Stock Ticker', type: 'text' },
        {
          identifier: 'txt_filer_name',
          title: 'Owner or Stock Name',
          type: 'keyword',
          selectedOption: 'all',
          options: [
            { text: 'Search All Fields', value: 'all' },
            { text: 'Search Stock Ticker', value: 'ric' },
            { text: 'Search Stock Name', value: 'name' },
            { text: 'Search Owner Names', value: 'organization_name' }
          ]
        },
        { identifier: 'owner_city', title: 'Owner City', type: 'text' },
        {
          identifier: 'holdings_date',
          title: 'Holding Date',
          type: 'date_range',
          items: [
            { id: 1, description: 'Past Month' },
            { id: 2, description: 'Past Year' }
          ]
        },
        { identifier: 'issuer_country', title: 'Issuer Country', items: [] },
        { identifier: 'organization_country', title: 'Owner Country', items: [] },
        { identifier: 'issuer_exchange', title: 'Exchange', items: useWwStore().global_exchanges },
        {
          identifier: 'investment_style',
          title: 'Fund Investing Style',
          items: [
            { id: 'Aggres. Gr.', description: 'Aggres. Growth' },
            { id: 'Broker-Dealer', description: 'Broker-Dealer' },
            { id: 'Core Growth', description: 'Core Growth' },
            { id: 'Core Value', description: 'Core Value' },
            { id: 'Deep Value', description: 'Deep Value' },
            { id: 'Emerg. Mkts.', description: 'Emerg. Mkts.' },
            { id: 'Emerging Markets Hedge', description: 'Emerging Markets Hedge' },
            { id: 'Fund of Funds Hedge', description: 'Fund of Funds Hedge' },
            { id: 'GARP', description: 'GARP' },
            { id: 'Global Macro', description: 'Global Macro' },
            { id: 'Growth', description: 'Growth' },
            { id: 'Hedge Fund', description: 'Hedge Fund' },
            { id: 'Income Value', description: 'Income Value' },
            { id: 'Index', description: 'Index' },
            { id: 'Long Bias', description: 'Long Bias' },
            { id: 'Long/Short', description: 'Long/Short' },
            { id: 'Market Neutral', description: 'Market Neutral' },
            { id: 'Mixed Style', description: 'Mixed Style' },
            { id: 'Momentum', description: 'Momentum' },
            { id: 'Sector Specific', description: 'Sector Specific' },
            { id: 'Specialty', description: 'Specialty' },
            { id: 'VC/Private Equity', description: 'VC/Private Equity' },
            { id: 'Yield', description: 'Yield' }
          ]
        },
        {
          identifier: 'investor_type',
          title: 'Type of Investor',
          items: [
            { id: 'Bank and Trust', description: 'Bank and Trust' },
            { id: 'Corporation', description: 'Corporation' },
            { id: 'Foundation', description: 'Foundation' },
            { id: 'Government Agency', description: 'Government Agency' },
            { id: 'Hedge Fund', description: 'Hedge Fund' },
            { id: 'Holding Company', description: 'Holding Company' },
            { id: 'Independent Research Firm', description: 'Independent Research Firm' },
            { id: 'Individual Investor', description: 'Individual Investor' },
            { id: 'Insurance Company', description: 'Insurance Company' },
            { id: 'Investment Advisor', description: 'Investment Advisor' },
            { id: 'Investment Advisor/Hedge Fund', description: 'Investment Advisor/Hedge Fund' },
            { id: 'Other Insider Investor', description: 'Other Insider Investor' },
            { id: 'Pension Fund', description: 'Pension Fund' },
            { id: 'Private Equity', description: 'Private Equity' },
            { id: 'Research Firm', description: 'Research Firm' },
            { id: 'Sovereign Wealth Fund', description: 'Sovereign Wealth Fund' },
            { id: 'Venture Capital', description: 'Venture Capital' }
          ]
        }
      ],
      filtersCounts: {},
      filtersParamsObject: {}
    }
  },

  computed: {
    params() {
      return {
        rows: this.pagination.itemsPerPage,
        page: this.pagination.page,
        sidx: this.pagination.sortBy[0]?.key,
        sord: this.pagination.sortBy[0]?.order || 'asc',
        ...this.filtersParamsObject
      }
    },
    visibleTableHeaders() {
      return this.tableHeaders.filter(h => h.visible)
    }
  },

  watch: {
    params: { deep: true, handler() { this.fetchData() } }
  },

  mounted() {
    this.filters[4].items = cloneDeep(useWwStore().global_countries)
    this.filters[5].items = cloneDeep(useWwStore().global_countries)
    this.filters[6].items = cloneDeep(useWwStore().global_exchanges)
    this.$refs.filtersBar.filtersProxy = cloneDeep(this.filters)
  },

  methods: {
    fetchData() {
      this.loading = true
      api.get('global_holdings_query', { params: this.params })
        .then(response => {
          this.tableItems = response.data.rows
          this.totalItems = response.data.records
          this.filtersCounts = response.data.facets
        })
        .catch(error => {
          useAppStore().showNotification({ type: 'error', text: (error.response?.data) || error.message })
        })
        .finally(() => { this.loading = false })
    }
  }
}
</script>
